import { combine, createEvent, createStore, sample } from 'effector'
import { produce } from 'immer'
import { model as sessionModel } from '~/entities/session'
import { appearanceFeature } from '~/features/appearance'
import { aye, nay } from '~/shared/helpers'
import { getComponentsFx } from './components-request'
import { Empty } from './index.h'
import {
  $structure,
  applyModuleStructure,
  initModule,
  loadModule,
  resetStructure,
  setStructure,
} from './model'

const $identifier = appearanceFeature.$currentIdentifier

/*
 * Dynamic and lazy loading list of components
 */

// first components load request count
const INITIAL_LOAD_COUNT = 12

// next components load requests count
const LAZY_LOAD_BY_COUNT = 12

export const loadMoreComponents = createEvent<{ identifier: string }>()
export const loadMoreCurrentComponents = createEvent()

// decide rows load needed after main store structure manual set
sample({
  clock: setStructure,
  // if some rows exist - then new rows load controled by "loadMoreComponents"
  filter: ({ structure }) => (structure ? structure.size === 0 : true),
  target: loadModule,
})

// Identifire: loaded components
const $loadedComponentsCount = createStore<Record<string, number>>({}).reset(
  resetStructure
)

// components loaded state // Identifire: loaded components
const componentsLoaded = createEvent<{ identifier: string; last: boolean }>()

export const $isComponentLoadedById = createStore<Record<string, boolean>>({})
  .on(componentsLoaded, (state, { identifier, last }) => ({
    ...state,
    [identifier]: last,
  }))
  .reset(resetStructure)

sample({
  clock: getComponentsFx.done,
  source: sessionModel.$guestReloginPending,
  filter: (guestReloginPending) => nay(guestReloginPending),
  fn: (_, { params, result }) => ({
    identifier: params.identifier,
    last: aye(result.last),
  }),
  target: componentsLoaded,
})

const componentsLoadFailed = createEvent<{ identifier: string }>()

const $failedComponentsLoadById = createStore<Record<string, boolean>>({})
  .on(componentsLoadFailed, (state, { identifier }) => ({
    ...state,
    [identifier]: true,
  }))
  .on(initModule, (state, { identifier }) => ({
    ...state,
    [identifier]: false,
  }))
  .reset(resetStructure)

sample({
  clock: getComponentsFx.fail,
  source: sessionModel.$guestReloginPending,
  filter: (guestReloginPending) => nay(guestReloginPending),
  fn: (_, { params }) => ({ identifier: params.identifier }),
  target: componentsLoadFailed,
})

export const $isComponentsLoadFailed = combine(
  $failedComponentsLoadById,
  $identifier,
  (failedStates, identifire) =>
    identifire ? aye(failedStates[identifire]) : false
)

export const $isComponentsLoaded = combine(
  $isComponentLoadedById,
  $identifier,
  (loadedStates, identifire) =>
    identifire ? aye(loadedStates[identifire]) : false
)

// need load more Rows on current page
sample({
  clock: loadMoreCurrentComponents,
  source: $identifier,
  filter: Boolean,
  fn: (identifier) => ({ identifier }),
  target: loadMoreComponents,
})

// initial components load request
sample({
  clock: loadModule,
  target: loadMoreComponents,
})

// first and next components load request
// - on the next requests loads page N with LAZY_LOAD_BY_COUNT components
// - on the first request loads page 0 with INITIAL_LOAD_COUNT components
sample({
  clock: loadMoreComponents,
  source: {
    loadedCount: $loadedComponentsCount,
    isLoaded: $isComponentLoadedById,
    isFailed: $failedComponentsLoadById,
    guestReloginPending: sessionModel.$guestReloginPending,
    pending: getComponentsFx.pending,
  },
  filter: (
    { isLoaded, isFailed, guestReloginPending, pending },
    { identifier }
  ) =>
    nay(isLoaded[identifier]) &&
    nay(isFailed[identifier]) &&
    nay(pending) &&
    nay(guestReloginPending),
  fn: ({ loadedCount }, { identifier }) => {
    const count = loadedCount[identifier] ?? 0

    return {
      page: Math.trunc(count / LAZY_LOAD_BY_COUNT),
      count: count === 0 ? INITIAL_LOAD_COUNT : LAZY_LOAD_BY_COUNT,
      identifier,
    }
  },
  target: getComponentsFx,
})

sample({
  clock: sessionModel.stopGuestReloginTransition,
  source: {
    identifier: $identifier,
    isLoaded: $isComponentLoadedById,
    pending: getComponentsFx.pending,
  },
  filter: ({ identifier, isLoaded, pending }) =>
    aye(identifier) && nay(isLoaded[identifier!]) && nay(pending),
  fn: ({ identifier }) => ({ identifier: identifier! }),
  target: loadMoreComponents,
})

// If a rows page is empty but not last, there is no rendered sentinel to
// trigger the next request.
sample({
  clock: getComponentsFx.done,
  source: sessionModel.$guestReloginPending,
  filter: (guestReloginPending, { result }) =>
    nay(guestReloginPending) &&
    nay(result.last) &&
    Array.isArray(result.content) &&
    result.content.length === 0,
  fn: (_, { params }) => ({
    page: params.page + 1,
    count: LAZY_LOAD_BY_COUNT,
    identifier: params.identifier,
  }),
  target: getComponentsFx,
})

// add empty content on components load (structure + origin atomically)
sample({
  clock: getComponentsFx.done,
  source: {
    structure: $structure,
    identifier: $identifier,
    guestReloginPending: sessionModel.$guestReloginPending,
  },
  filter: ({ identifier, guestReloginPending }, { params }) =>
    identifier === params.identifier && nay(guestReloginPending),
  fn: ({ structure }, { result }) => {
    const updated = structure ? new Map(structure) : new Map()

    if (Array.isArray(result.content)) {
      for (const comp of result.content) {
        const exist = updated.get(comp.id)

        if (exist) {
          // Update description only
          updated.set(comp.id, { ...exist, desc: comp })
        } else {
          // Insert empty data for new components
          updated.set(comp.id, { data: Empty(), desc: comp })
        }
      }
    }

    return {
      structure: updated,
      structureOrigin: result.structureOrigin ?? 'backend',
    }
  },
  target: applyModuleStructure,
})

sample({
  clock: getComponentsFx.done,
  source: {
    loadedCount: $loadedComponentsCount,
    guestReloginPending: sessionModel.$guestReloginPending,
  },
  filter: ({ guestReloginPending }) => nay(guestReloginPending),
  fn: ({ loadedCount }, { params }) =>
    produce(loadedCount, (draft) => {
      const { page, count, identifier } = params
      draft[identifier] = page * count + count
    }),
  target: $loadedComponentsCount,
})
